'use client';

import { cn } from '@ui/lib/utils';
import { X } from 'lucide-react';
import { useState, useRef, useEffect, KeyboardEvent } from 'react';

export interface TagInputProps {
    value: string; // Comma-separated string
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    autocomplete?: boolean;
    onSearch?: (query: string) => Promise<string[]>;
    disabled?: boolean;
}

export function TagInput({
    value,
    onChange,
    placeholder = 'Add tags...',
    className,
    autocomplete = false,
    onSearch,
    disabled = false,
}: TagInputProps) {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Parse tags from comma-separated string
    const tags = value
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

    // Fetch suggestions when input changes
    useEffect(() => {
        if (autocomplete && onSearch && inputValue.length > 0) {
            const timeoutId = setTimeout(async () => {
                try {
                    const results = await onSearch(inputValue);
                    setSuggestions(results);
                    setShowSuggestions(results.length > 0);
                } catch (error) {
                    console.error('Error fetching suggestions:', error);
                    setSuggestions([]);
                }
            }, 300); // Debounce

            return () => clearTimeout(timeoutId);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    }, [inputValue, autocomplete, onSearch]);

    // Close suggestions on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        };

        if (showSuggestions) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSuggestions]);

    const addTag = (tag: string) => {
        const trimmedTag = tag.trim();
        if (trimmedTag && !tags.includes(trimmedTag)) {
            const newTags = [...tags, trimmedTag];
            onChange(newTags.join(', '));
        }
        setInputValue('');
        setShowSuggestions(false);
    };

    const removeTag = (indexToRemove: number) => {
        const newTags = tags.filter((_, index) => index !== indexToRemove);
        onChange(newTags.join(', '));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            if (inputValue.trim()) {
                addTag(inputValue);
            }
        } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
            removeTag(tags.length - 1);
        }
    };

    const handleSuggestionClick = (suggestion: string) => {
        addTag(suggestion);
        inputRef.current?.focus();
    };

    return (
        <div className={cn('relative', className)} ref={containerRef}>
            <div
                className={cn(
                    'min-h-[42px] w-full bg-white border border-gray-300 rounded-md shadow-sm',
                    'px-2 py-1.5 flex flex-wrap gap-2 items-center',
                    'focus-within:ring-2 focus-within:ring-gray-500 focus-within:border-gray-500',
                    'transition-colors duration-200',
                    disabled && 'opacity-50 cursor-not-allowed bg-gray-50'
                )}
                onClick={() => !disabled && inputRef.current?.focus()}
            >
                {tags.map((tag, index) => (
                    <span
                        key={index}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 rounded-md text-sm font-medium"
                    >
                        {tag}
                        {!disabled && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeTag(index);
                                }}
                                className="hover:bg-gray-200 rounded-full p-0.5 transition-colors"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        )}
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={tags.length === 0 ? placeholder : ''}
                    disabled={disabled}
                    className="flex-1 min-w-[120px] outline-none bg-transparent text-sm"
                />
            </div>

            {/* Autocomplete suggestions */}
            {showSuggestions && suggestions.length > 0 && (
                <div className="absolute mt-1 w-full rounded-md bg-white shadow-lg z-50">
                    <ul className="max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                        {suggestions.map((suggestion, index) => (
                            <li
                                key={index}
                                className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-600 hover:text-white transition-colors text-gray-900"
                                onClick={() => handleSuggestionClick(suggestion)}
                            >
                                <span className="font-normal block truncate">{suggestion}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
